import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import DetailInfo from './components/LabList/DetailInfo';
import Filter from './components/Filters/Filter';
import { LabExam } from './LabExam';
import { getApiV1Url } from './config/api';

function LabExamsTable({ weldedJointsOnly = false }: { weldedJointsOnly?: boolean }) {
  const [items, setItems] = useState<LabExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Состояния фильтров
  const [filteredExamResults, setFilteredExamResults] = useState<string[]>([]);
  const [filteredYears, setFilteredYears] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [sortField, setSortField] = useState<keyof LabExam | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterFilters, setFilterFilters] = useState<{
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType: string[];
  }>({
    manufacturerName: [],
    manufacturerInn: [],
    region: [],
    objectAddress: [],
    weldingJointType: [],
  });

  const STORAGE_KEY = weldedJointsOnly ? 'embed_welded_joint_exams_filters_v1' : 'embed_lab_exams_filters_v1';

  useEffect(() => {
    axios
      .get(getApiV1Url('LAB_EXAMS_LIST'))
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setItems(rows.filter((item: LabExam) =>
          weldedJointsOnly ? !!item.welding_joint_type?.trim() : !item.welding_joint_type?.trim()
        ));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [weldedJointsOnly]);

  // Загрузка сохраненных фильтров
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) || {};
      const ensureArray = (v: any) => Array.isArray(v) ? v : [];
      setFilterFilters({
        manufacturerName: ensureArray(saved.filters?.manufacturerName),
        manufacturerInn: ensureArray(saved.filters?.manufacturerInn),
        region: ensureArray(saved.filters?.region),
        objectAddress: ensureArray(saved.filters?.objectAddress),
        weldingJointType: ensureArray(saved.filters?.weldingJointType),
      });
      setFilteredExamResults(ensureArray(saved.filteredExamResults));
      setFilteredYears(ensureArray(saved.filteredYears));
      setSelectedRegions(ensureArray(saved.selectedRegions));
      setSortField(saved.sortField || null);
      setSortDirection(saved.sortDirection || 'asc');
    } catch {}
  }, []);

  // Сохранение фильтров при изменении
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        filters: filterFilters,
        filteredExamResults,
        filteredYears,
        selectedRegions,
        sortField,
        sortDirection,
      }));
    } catch {}
  }, [filterFilters, filteredExamResults, filteredYears, selectedRegions, sortField, sortDirection]);

  // Функция сортировки
  const sortData = useCallback((data: LabExam[]) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortField === 'inn_company') {
        const aNum = parseFloat(String(aValue || '')) || 0;
        const bNum = parseFloat(String(bValue || '')) || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortField, sortDirection]);

  // Логика фильтрации
  const applyAllFilters = useMemo(() => {
    return (data: LabExam[]) => {
      let filtered = [...data];

      if (filterFilters.manufacturerName.length > 0) {
        filtered = filtered.filter(item =>
          item.name_company && filterFilters.manufacturerName.some(name =>
            item.name_company.toLowerCase().includes(name.toLowerCase())
          )
        );
      }

      if (filterFilters.manufacturerInn.length > 0) {
        filtered = filtered.filter(item =>
          item.inn_company && filterFilters.manufacturerInn.some(inn =>
            item.inn_company.toLowerCase().includes(inn.toLowerCase())
          )
        );
      }

      if (filterFilters.region.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company && filterFilters.region.some(region =>
            item.region_company.toLowerCase().includes(region.toLowerCase())
          )
        );
      }

      if (filterFilters.objectAddress.length > 0) {
        filtered = filtered.filter(item =>
          item.object_adres && filterFilters.objectAddress.some(address =>
            item.object_adres.toLowerCase().includes(address.toLowerCase())
          )
        );
      }

      if (filterFilters.weldingJointType.length > 0) {
        filtered = filtered.filter(item =>
          item.welding_joint_type && filterFilters.weldingJointType.includes(item.welding_joint_type)
        );
      }

      if (filteredExamResults.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_result && filteredExamResults.includes(item.exam_result)
        );
      }

      if (filteredYears.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_date && filteredYears.includes(item.exam_date.slice(0, 4))
        );
      }

      if (selectedRegions.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company && selectedRegions.some(region =>
            region && item.region_company.toLowerCase() === region.toLowerCase()
          )
        );
      }

      return sortData(filtered);
    };
  }, [filterFilters, filteredExamResults, filteredYears, selectedRegions, sortData]);

  const [filteredItems, setFilteredItems] = useState<LabExam[]>([]);

  useEffect(() => {
    const filtered = applyAllFilters(items);
    setFilteredItems(filtered);
  }, [items, applyAllFilters]);

  // Обработчики фильтров
  const handleFilterChange = (newFilters: {
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType?: string[];
  }) => {
    setFilterFilters({
      manufacturerName: newFilters.manufacturerName,
      manufacturerInn: newFilters.manufacturerInn,
      region: newFilters.region,
      objectAddress: newFilters.objectAddress,
      weldingJointType: newFilters.weldingJointType || [],
    });
  };

  const handleExamResultChange = (results: string[]) => {
    setFilteredExamResults(results);
  };

  const handleResetFilters = () => {
    setSelectedRegions([]);
    setFilteredExamResults([]);
    setFilteredYears([]);
    setFilterFilters({
      manufacturerName: [],
      manufacturerInn: [],
      region: [],
      objectAddress: [],
      weldingJointType: [],
    });
    setFilteredItems(items);
  };

  const handleRegionSelect = (region: string) => {
    if (!region || !items) return;

    const exactMatch = items.find((item: LabExam) =>
      item.region_company && region && item.region_company.toLowerCase() === region.toLowerCase()
    );

    const partialMatch = !exactMatch ? items.find((item: LabExam) =>
      item.region_company && region && (
        item.region_company.toLowerCase().includes(region.toLowerCase()) ||
        region.toLowerCase().includes(item.region_company.toLowerCase())
      )
    ) : null;

    const matchedRegion = exactMatch ? exactMatch.region_company : (partialMatch ? partialMatch.region_company : null);

    if (matchedRegion) {
      const newSelectedRegions = selectedRegions.some((r: string) => r && matchedRegion && r.toLowerCase() === matchedRegion.toLowerCase())
        ? selectedRegions.filter((r: string) => r && matchedRegion && r.toLowerCase() !== matchedRegion.toLowerCase())
        : [...selectedRegions, matchedRegion];

      setSelectedRegions(newSelectedRegions);
    }
  };

  // Обработчик сортировки
  const handleSort = useCallback((field: keyof LabExam) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // При третьем нажатии сброс
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: keyof LabExam) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  }, [sortField, sortDirection]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999
      }}>
        <img
          src="/loading.png"
          alt="Загрузка..."
          style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
        />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const noResults = filteredItems.length === 0;

  return (
    <div style={{ padding: 16 }} data-embed="true">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>{weldedJointsOnly ? 'Реестр испытаний сварных соединений' : 'Лабораторные испытания'}</h2>
      </div>
      
      {isFilterOpen && (
        <div className="labexam-filter-modal-overlay">
          <div className="labexam-filter-modal" style={{ overflowY: 'visible' }}>
            <button className="labexam-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
              ×
            </button>
            <Filter
              data={items}
              onFilterChange={handleFilterChange}
              examResults={filteredExamResults}
              onExamResultChange={handleExamResultChange}
              onRegionSelect={handleRegionSelect}
              selectedRegions={selectedRegions}
              onResetFilters={handleResetFilters}
              initialFilters={filterFilters}
              open={isFilterOpen}
              showWeldingJointType={weldedJointsOnly}
            />
          </div>
        </div>
      )}
      
      {noResults ? (
        <div style={{
          marginTop: 24,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          color: '#374151'
        }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
          <div>По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.</div>
        </div>
      ) : (
        <DetailInfo 
          currentItems={filteredItems} 
          sortField={sortField} 
          sortDirection={sortDirection} 
          onSort={handleSort} 
          onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
          weldedJointsOnly={weldedJointsOnly}
        />
      )}
    </div>
  );
}

export default LabExamsTable;


